/**
 * A-Roll SaaS interface localisation (client surface only — the KG CRM
 * stays English).
 *
 * Twelve locales, matching the caption-translation menu. English is the
 * source of truth and ships in the main bundle; every other dictionary
 * is lazy-loaded on first use so English sessions pay nothing. Missing
 * keys fall back to English, so a half-shipped dictionary degrades to
 * mixed language rather than blank UI.
 *
 * Detection order: localStorage `aroll.locale` → the browser's language
 * list → English. The Settings picker writes both localStorage and (for
 * signed-in users) `User.locale`, which the server uses to localise
 * transactional email.
 *
 * Keys are flat, prefixed per surface ("settings.plan.title"). Plurals
 * use explicit `<key>_one` / `<key>_other` pairs via tp() — no CLDR
 * machinery for a surface this size.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { en, type ArollKey } from '@/locales/aroll/en';

export const AROLL_LOCALES = {
  en: { label: 'English', dir: 'ltr' },
  es: { label: 'Español', dir: 'ltr' },
  fr: { label: 'Français', dir: 'ltr' },
  de: { label: 'Deutsch', dir: 'ltr' },
  it: { label: 'Italiano', dir: 'ltr' },
  pt: { label: 'Português', dir: 'ltr' },
  nl: { label: 'Nederlands', dir: 'ltr' },
  ja: { label: '日本語', dir: 'ltr' },
  ko: { label: '한국어', dir: 'ltr' },
  zh: { label: '中文（简体）', dir: 'ltr' },
  hi: { label: 'हिन्दी', dir: 'ltr' },
  ar: { label: 'العربية', dir: 'rtl' },
} as const;

export type ArollLocale = keyof typeof AROLL_LOCALES;

const STORAGE_KEY = 'aroll.locale';

/** Map a BCP-47 tag ("pt-BR", "zh-Hans-CN") onto one of our locales. */
function matchLocale(tag: string): ArollLocale | null {
  const primary = tag.toLowerCase().split('-')[0] ?? '';
  return primary in AROLL_LOCALES ? (primary as ArollLocale) : null;
}

export function detectArollLocale(): ArollLocale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && stored in AROLL_LOCALES) return stored as ArollLocale;
  } catch {
    /* storage unavailable (private mode etc.) — fall through */
  }
  for (const tag of navigator.languages ?? [navigator.language]) {
    const hit = tag && matchLocale(tag);
    if (hit) return hit;
  }
  return 'en';
}

type Dict = Partial<Record<ArollKey, string>>;

// Lazy loaders keep the 11 non-English dictionaries out of the main
// bundle. Vite turns each into its own chunk.
const LOADERS: Record<Exclude<ArollLocale, 'en'>, () => Promise<{ dict: Dict }>> = {
  es: () => import('@/locales/aroll/es'),
  fr: () => import('@/locales/aroll/fr'),
  de: () => import('@/locales/aroll/de'),
  it: () => import('@/locales/aroll/it'),
  pt: () => import('@/locales/aroll/pt'),
  nl: () => import('@/locales/aroll/nl'),
  ja: () => import('@/locales/aroll/ja'),
  ko: () => import('@/locales/aroll/ko'),
  zh: () => import('@/locales/aroll/zh'),
  hi: () => import('@/locales/aroll/hi'),
  ar: () => import('@/locales/aroll/ar'),
};

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (m, k: string) =>
    k in vars ? String(vars[k]) : m,
  );
}

type I18nValue = {
  locale: ArollLocale;
  /** BCP-47-ish tag for Date/number formatting. */
  dateLocale: string;
  dir: 'ltr' | 'rtl';
  setLocale: (l: ArollLocale) => void;
  t: (key: ArollKey, vars?: Record<string, string | number>) => string;
  /** Plural-aware t: resolves `<key>_one` when n is 1, else
   *  `<key>_other`; {n} is always available to the template. */
  tp: (key: string, n: number, vars?: Record<string, string | number>) => string;
};

const I18nContext = createContext<I18nValue | null>(null);

export function ArollLocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<ArollLocale>(() => detectArollLocale());
  const [dict, setDict] = useState<Dict>(en);

  useEffect(() => {
    let cancelled = false;
    if (locale === 'en') {
      setDict(en);
      return;
    }
    LOADERS[locale]()
      .then((m) => {
        if (!cancelled) setDict(m.dict);
      })
      .catch(() => {
        // A failed chunk load leaves English in place — never blank UI.
        if (!cancelled) setDict(en);
      });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const setLocale = useCallback((l: ArollLocale) => {
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* non-persistent is fine */
    }
    setLocaleState(l);
  }, []);

  const value = useMemo<I18nValue>(() => {
    const t = (key: ArollKey, vars?: Record<string, string | number>) =>
      interpolate(dict[key] ?? en[key] ?? key, vars);
    const tp = (key: string, n: number, vars?: Record<string, string | number>) => {
      const k = (n === 1 ? `${key}_one` : `${key}_other`) as ArollKey;
      return t(k, { n, ...vars });
    };
    return {
      locale,
      dateLocale: locale === 'zh' ? 'zh-Hans' : locale,
      dir: AROLL_LOCALES[locale].dir,
      setLocale,
      t,
      tp,
    };
  }, [locale, dict, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useT must be used inside ArollLocaleProvider');
  }
  return ctx;
}
